import { getHtmlLanguage } from "utils/helpers"
import { getFailedRequestInfo } from "utils/server-api"
import { ScreenType } from "models/tables"
import {
  PlayError,
  PlayErrorAction,
  PlayErrorLevel,
  PlayHubState,
} from "play/play-model"

// What the player is told when a server request fails is deliberately short:
// a friendly sentence and a reference such as "482913 · 14:32:07 · K7M2"
// (game code, time, code). Everything needed to diagnose the failure is sent
// to the server under that code (api/playhub/client-error) and read in
// Vitosha → Грешки → Грешки в играта, so a screenshot of the message is enough.

// Letters only, so the code stands apart from the game code and the time
// beside it; consonants only, so it never spells a word on a child's screen.
const CODE_ALPHABET = "BCDFGHJKLMNPQRSTVWXZ"
const CODE_LENGTH = 4

const MAX_TEXT = 2000

export function newErrorCode(): string {
  let code = ""
  for (let i = 0; i < CODE_LENGTH; i++) {
    code += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)]
  }
  return code
}

function pad(n: number) {
  return n < 10 ? "0" + n : "" + n
}

function cut(text: string | null): string | null {
  if (text === null || text.length === 0) return null
  return text.length <= MAX_TEXT ? text : text.substring(0, MAX_TEXT)
}

function asText(value: unknown): string | null {
  if (value === null || typeof value === "undefined") return null
  if (typeof value === "string") return value
  try {
    return JSON.stringify(value)
  } catch (e) {
    return String(value)
  }
}

/**
 * The error text of a rejected request. The rejection is whatever the server
 * answered (a ProblemDetails, an ErrorResult around an HTML page) or a string
 * thrown by our own guards.
 */
function errorMessage(e: unknown): string | null {
  if (typeof e === "object" && e !== null) {
    const obj = e as any
    const text = obj.detail ?? obj.title ?? obj.message
    if (typeof text === "string" && text.length > 0) return text
  }
  return asText(e)
}

function errorBody(e: unknown): string | null {
  if (typeof e === "object" && e !== null && "body" in e) {
    return asText((e as any).body)
  }
  return null
}

/**
 * The session's refusal of an answer or a "next screen" for a screen the
 * player has passed (GameError.AlreadyAnswered, a 400 from PlayHubController).
 */
function isAlreadyAnswered(statusCode: number | undefined, e: unknown) {
  return (
    statusCode === 400 &&
    typeof e === "object" &&
    e !== null &&
    (e as any).detail === "AlreadyAnswered"
  )
}

/** Describes a failure for the player and for the report. Pure, apart from the clock and the random code. */
export function createPlayError(
  action: PlayErrorAction,
  e: unknown,
  level: PlayErrorLevel = "action"
): PlayError {
  const info = getFailedRequestInfo(e)
  const now = new Date()

  if (level === "action" && isAlreadyAnswered(info?.statusCode, e)) {
    level = "behind"
  }

  return {
    code: newErrorCode(),
    level,
    action,
    time: now.toISOString(),
    shownTime: `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`,
    method: info?.method ?? null,
    url: info?.url ?? null,
    statusCode: info?.statusCode ?? 0,
    requestId: info?.requestId ?? null,
    message: cut(errorMessage(e)),
    responseBody: cut(errorBody(e)),
  }
}

/**
 * The game code as shown next to the error code. Demo and single-seat codes
 * are guids: too long to show, and the error code finds the report anyway.
 */
export function shownGameCode(gameCode: string | null | undefined) {
  return !!gameCode && gameCode.length <= 8 ? gameCode : null
}

/** The body of POST api/playhub/client-error (PlayClientErrorRequest on the server) */
export interface PlayErrorReport {
  code: string
  clientTime: string
  shownTime: string
  gameCode: string | null
  connectionId: string | null
  isTeacher: boolean
  action: PlayErrorAction
  method: string | null
  url: string | null
  statusCode: number
  traceId: string | null
  screenId: string | null
  screenIndex: number | null
  screenType: string | null
  message: string | null
  details: { [key: string]: unknown }
  pageUrl: string
}

export function buildErrorReport(
  error: PlayError,
  state: PlayHubState
): PlayErrorReport {
  const screen = state.screen

  return {
    code: error.code,
    clientTime: error.time,
    shownTime: error.shownTime,
    gameCode: state.gameCode || null,
    connectionId: state.connectionId || null,
    isTeacher: state.isTeacherUrl,
    action: error.action,
    method: error.method,
    url: error.url,
    statusCode: error.statusCode,
    traceId: error.requestId,
    screenId: screen?.id ?? null,
    screenIndex: typeof state.screenIndex === "number" ? state.screenIndex : null,
    screenType: screen ? (ScreenType[screen.screenType] ?? String(screen.screenType)) : null,
    message: error.message,
    details: {
      level: error.level,
      responseBody: error.responseBody,
      mode: state.mode,
      internalPage: state.internalPage,
      loadingReason: state.loadingReason,
      isReview: state.isReview,
      finished: state.finished,
      countScreens: state.countScreens,
      online: navigator.onLine,
    },
    pageUrl: location.href,
  }
}

///////////////////////////////////////////////////////////////////////////////
// Sending
//
// A report is made when a request failed, which is often when the network is
// down, so it is kept (in localStorage too: "reload the page" is what a
// lost connection asks for) and sent again until the server has taken it.
///////////////////////////////////////////////////////////////////////////////

const STORAGE_KEY = "cb-play-error-reports"
const MAX_QUEUED = 20
const FIRST_RETRY_MS = 5_000
const MAX_RETRY_MS = 60_000

let s_queue: PlayErrorReport[] | null = null
let s_sending = false
let s_retryTimer: ReturnType<typeof setTimeout> | null = null
let s_retryMs = FIRST_RETRY_MS
let s_listening = false

function queue(): PlayErrorReport[] {
  if (s_queue === null) {
    s_queue = []
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]")
      if (Array.isArray(stored)) s_queue = stored
    } catch (e) {}
  }
  return s_queue
}

function saveQueue() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue()))
  } catch (e) {}
}

function isSame(a: PlayErrorReport, b: PlayErrorReport) {
  return a.code === b.code && a.clientTime === b.clientTime
}

export function queueErrorReport(report: PlayErrorReport) {
  const q = queue()
  if (q.some((x) => isSame(x, report))) return

  q.push(report)
  if (q.length > MAX_QUEUED) {
    q.splice(0, q.length - MAX_QUEUED)
  }
  saveQueue()
  sendQueuedErrorReports()
}

/** Sends what is waiting, and keeps trying. Call once at start for the reports a previous page left behind. */
export function sendQueuedErrorReports() {
  if (!s_listening) {
    s_listening = true
    window.addEventListener("online", () => sendQueuedErrorReports())
  }

  if (s_sending || queue().length === 0) return
  s_sending = true

  if (s_retryTimer) {
    clearTimeout(s_retryTimer)
    s_retryTimer = null
  }

  sendAll()
    .then(() => {
      s_retryMs = FIRST_RETRY_MS
    })
    .catch(() => {
      s_retryTimer = setTimeout(sendQueuedErrorReports, s_retryMs)
      s_retryMs = Math.min(s_retryMs * 2, MAX_RETRY_MS)
    })
    .finally(() => {
      s_sending = false
    })
}

async function sendAll() {
  const url = `/${getHtmlLanguage()}/api/playhub/client-error`
  const q = queue()

  while (q.length > 0) {
    const report = q[0]

    // fetch, not serverPost: a failure here must not become a report itself
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(report),
      credentials: "same-origin",
      keepalive: true,
    })

    // taken, or refused for good (4xx): either way it is not sent again.
    // 429 and 5xx are worth another try later.
    const refused =
      response.status >= 400 && response.status < 500 && response.status !== 429
    if (!response.ok && !refused) {
      throw new Error(`client-error report: ${response.status}`)
    }

    const idx = q.findIndex((x) => isSame(x, report))
    if (idx !== -1) q.splice(idx, 1)
    saveQueue()
  }
}
